import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
} from "react";
import {
  computePlaybackDurationMs,
  createMidiOutputEngine,
  createPlaybackController,
  createSoundfontEngine,
  getMidiOutputPort,
  secondsToTick,
  tickToSeconds,
  type EngineState,
  type MidiOutputPort,
  type SoundfontSource,
} from "@midi-fantom-utils/player-core";
import { tickToMBT, type PlaybackData } from "@midi-fantom-utils/midi-util";
import type { Midi, MidiTimeSignature } from "@midi-fantom-utils/midi-core";
import type { MidiPortSelection, SoundOutput } from "../config.js";
import { TimeBox } from "./TimeBox.js";
import s from "./TimelinePlayer.module.css";

export type TimelinePlayerProps = {
  midi: Midi;
  playback: PlaybackData;
  fileName: string;
  source: SoundfontSource;
  output: SoundOutput;
  midiPort: MidiPortSelection | null;
  onOpenSettings?: () => void;
  onTickChange?: (tick: number) => void;
};

/**
 * Собственная обёртка над движком (`createSoundfontEngine` для SF2 или
 * `createMidiOutputEngine` для MIDI-выхода) + `createPlaybackController`.
 * В отличие от `MidiPlayer`, отдаёт наружу `currentTick` через `onTickChange`
 * — это нужно для playhead на piano roll таймлайна.
 *
 * MIDI-режим: без AudioContext (контроллер работает на `performance.now()`-
 * часах), без громкости (слайсер скрыт) и без прогресса загрузки SF2.
 */
export const TimelinePlayer = ({
  midi,
  playback,
  fileName,
  source,
  output,
  midiPort,
  onOpenSettings,
  onTickChange,
}: TimelinePlayerProps) => {
  const isMidi = output === "midi";
  const [audioCtx, setAudioCtx] = useState<AudioContext | null>(null);
  const [engineState, setEngineState] = useState<EngineState>(EMPTY_ENGINE_STATE);
  const [positionMs, setPositionMs] = useState(0);
  const [durationMs, setDurationMs] = useState(0);
  const [status, setStatus] = useState<string>("idle");
  const [volume, setVolume] = useState(0.8);
  const [pendingPlay, setPendingPlay] = useState(false);
  // Разрешённый MIDI-порт (MidiOutputPort) для выбранного `midiPort`.
  const [midiPortInstance, setMidiPortInstance] = useState<MidiOutputPort | null>(
    null
  );
  const masterGainRef = useRef<GainNode | null>(null);
  const engineRef = useRef<ReturnType<typeof createSoundfontEngine> | null>(null);
  const controllerRef = useRef<ReturnType<typeof createPlaybackController> | null>(null);
  const midiEngineRef = useRef<ReturnType<typeof createMidiOutputEngine> | null>(null);
  const midiControllerRef = useRef<ReturnType<typeof createPlaybackController> | null>(null);
  const volumeRef = useRef(volume);
  const playbackRef = useRef(playback);
  const pendingPlayRef = useRef(false);

  useEffect(() => {
    playbackRef.current = playback;
  }, [playback]);
  useEffect(() => {
    volumeRef.current = volume;
    const gain = masterGainRef.current;
    const ctx = audioCtx;
    if (gain && ctx) {
      gain.gain.setTargetAtTime(volume, ctx.currentTime, 0.01);
    }
  }, [volume, audioCtx]);

  // Compute duration via общий `computePlaybackDurationMs` из player-core —
  // единая точка правды для edge-cases (отрицательные startTicks,
  // length-0 tempos), синхронизированная с MidiPlayer.
  useEffect(() => {
    setDurationMs(computePlaybackDurationMs(playback));
  }, [playback]);

  // MIDI-режим: резолвим порт для сохранённого выбора (id/name). Если порт
  // недоступен/доступ не выдан — `midiPortInstance` остаётся null, в UI
  // показываем баннер с подсказкой.
  useEffect(() => {
    if (!isMidi) {
      setMidiPortInstance(null);
      return;
    }
    let cancelled = false;
    getMidiOutputPort(midiPort).then((port) => {
      if (cancelled) return;
      setMidiPortInstance(port);
    });
    return () => {
      cancelled = true;
    };
  }, [isMidi, midiPort]);

  // Create SF2 engine + controller when AudioContext appears (SF2-режим).
  useEffect(() => {
    if (isMidi || !audioCtx) return;
    const gain = audioCtx.createGain();
    gain.gain.value = volumeRef.current;
    gain.connect(audioCtx.destination);
    masterGainRef.current = gain;

    const engine = createSoundfontEngine({
      audioCtx,
      source,
      notes: playback.notes,
      destination: gain,
      defer: true,
    });
    engineRef.current = engine;
    setEngineState({ ...engine.getState() });
    const engUnsub = engine.subscribe(() => setEngineState({ ...engine.getState() }));

    const controller = createPlaybackController({
      audioCtx,
      getNotes: () => playbackRef.current.notes,
      tempos: playbackRef.current.tempos,
      ppq: playbackRef.current.ppq,
      onScheduleNote: (note, when) => {
        const t = playbackRef.current.tempos;
        const p = playbackRef.current.ppq;
        const durSec = Math.max(0.05, tickToSeconds(note.durationTicks, t, p));
        engine.playNote(note, when, durSec);
      },
      onStopAll: () => engine.stopAll(),
    });
    controllerRef.current = controller;
    const ctlUnsub = controller.subscribe(() => {
      const s = controller.getState();
      setStatus(s.status);
      setPositionMs(s.positionMs);
    });

    return () => {
      engUnsub();
      ctlUnsub();
      controller.dispose();
      engine.dispose();
      controllerRef.current = null;
      engineRef.current = null;
      masterGainRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioCtx, isMidi]);

  // MIDI-режим: движок + контроллер на performance-часах, когда порт готов.
  // Пересоздаём при смене порта или playback (новый файл/инструменты).
  useEffect(() => {
    if (!isMidi || !midiPortInstance) {
      if (midiControllerRef.current) {
        midiControllerRef.current.dispose();
        midiControllerRef.current = null;
      }
      if (midiEngineRef.current) {
        midiEngineRef.current.dispose();
        midiEngineRef.current = null;
      }
      return;
    }
    const engine = createMidiOutputEngine({
      port: midiPortInstance,
      notes: playback.notes,
    });
    midiEngineRef.current = engine;

    const controller = createPlaybackController({
      clock: {
        now: () => performance.now() / 1000,
        resume: () => Promise.resolve(),
      },
      getNotes: () => playbackRef.current.notes,
      tempos: playbackRef.current.tempos,
      ppq: playbackRef.current.ppq,
      onScheduleNote: (note, when) => {
        const t = playbackRef.current.tempos;
        const p = playbackRef.current.ppq;
        const durSec = Math.max(0.05, tickToSeconds(note.durationTicks, t, p));
        engine.playNote(note, when, durSec);
      },
      onStopAll: () => engine.stopAll(),
    });
    midiControllerRef.current = controller;
    const ctlUnsub = controller.subscribe(() => {
      const s = controller.getState();
      setStatus(s.status);
      setPositionMs(s.positionMs);
    });

    // «Отложенный play»: нажали ▶, пока порт резолвился.
    if (pendingPlayRef.current) {
      pendingPlayRef.current = false;
      setPendingPlay(false);
      controller.play();
    }

    return () => {
      ctlUnsub();
      controller.dispose();
      engine.dispose();
      midiControllerRef.current = null;
      midiEngineRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMidi, midiPortInstance, playback]);

  // Перезагрузка движка при смене `source`/`playback` (новый soundfont или
  // изменённые инструменты каналов): `setInput` пересоздаёт все
  // Soundfont2-инстансы, поэтому депсы ограничены только этими двумя — не
  // вызываем на каждый рендер. Только SF2-режим (MIDI пересоздаётся выше).
  useEffect(() => {
    if (isMidi || !audioCtx) return;
    const engine = engineRef.current;
    const gain = masterGainRef.current;
    if (!engine || !gain) return;
    engine.setInput({ source, notes: playback.notes, destination: gain });
  }, [isMidi, audioCtx, source, playback]);

  // Выполняем отложенный play (SF2), когда движок готов (isReady).
  useEffect(() => {
    if (isMidi) return;
    if (!pendingPlayRef.current) return;
    const controller = controllerRef.current;
    const engine = engineRef.current;
    if (!controller || !engine || !engine.getState().isReady) return;
    pendingPlayRef.current = false;
    setPendingPlay(false);
    controller.play();
  }, [engineState.isReady, isMidi]);

  // Если загрузка звука упала с ошибкой, а play ещё ожидает — снимаем блокировку,
  // иначе кнопка осталась бы неактивной навсегда.
  useEffect(() => {
    if (engineState.error && pendingPlayRef.current && !isMidi) {
      pendingPlayRef.current = false;
      setPendingPlay(false);
    }
  }, [engineState.error, isMidi]);

  const ensureAudioContext = useCallback((): AudioContext | null => {
    if (audioCtx) return audioCtx;
    if (typeof window === "undefined") return null;
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!Ctor) return null;
    const ctx = new Ctor();
    void ctx.resume().catch(() => undefined);
    setAudioCtx(ctx);
    return ctx;
  }, [audioCtx]);

  const handlePlay = () => {
    if (isMidi) {
      if (midiPortInstance) {
        const controller = midiControllerRef.current;
        if (controller) {
          pendingPlayRef.current = false;
          setPendingPlay(false);
          controller.play();
        } else {
          // Порт есть, но контроллер ещё создаётся в эффекте.
          pendingPlayRef.current = true;
          setPendingPlay(true);
        }
      } else {
        // Порта нет — показываем баннер (рендер), play не блокируем.
        pendingPlayRef.current = false;
        setPendingPlay(false);
      }
      return;
    }
    const ctx = ensureAudioContext();
    if (!ctx) return;
    if (ctx.state === "suspended") void ctx.resume().catch(() => undefined);
    const controller = controllerRef.current;
    const engine = engineRef.current;
    if (controller && engine && engine.getState().isReady) {
      pendingPlayRef.current = false;
      setPendingPlay(false);
      controller.play();
    } else {
      // Контроллер/движок ещё не созданы или звуки загружаются — дождёмся
      // готовности в эффекте ниже и запустим там.
      pendingPlayRef.current = true;
      setPendingPlay(true);
    }
  };
  const handlePause = () => {
    pendingPlayRef.current = false;
    setPendingPlay(false);
    if (isMidi) midiControllerRef.current?.pause();
    else controllerRef.current?.pause();
  };
  const handleStop = () => {
    pendingPlayRef.current = false;
    setPendingPlay(false);
    if (isMidi) midiControllerRef.current?.stop();
    else controllerRef.current?.stop();
  };
  const handleSeek = (e: ChangeEvent<HTMLInputElement>) => {
    const v = Number.parseInt(e.target.value, 10);
    if (Number.isFinite(v)) {
      if (isMidi) midiControllerRef.current?.seek(v);
      else controllerRef.current?.seek(v);
    }
  };
  const handleVolume = (e: ChangeEvent<HTMLInputElement>) => {
    const v = Math.max(0, Math.min(1, Number.parseFloat(e.target.value)));
    setVolume(v);
  };

  // Convert positionMs to tick via secondsToTick — учитывает полную
  // карту темпо (а не только `tempos[0]`), что важно для multi-tempo
  // MIDI. Та же функция, что использует MidiPlayer внутри.
  const currentTick = useMemo(() => {
    if (positionMs <= 0) return 0;
    return Math.round(
      secondsToTick(positionMs / 1000, playback.tempos, playback.ppq)
    );
  }, [positionMs, playback]);

  useEffect(() => {
    if (onTickChange) onTickChange(currentTick);
  }, [currentTick, onTickChange]);

  const timeSigs: ReadonlyArray<MidiTimeSignature> = midi.header.timeSignatures;
  const totalTicks = useMemo(() => {
    if (playback.notes.length === 0) return 0;
    return playback.notes.reduce(
      (max, n) => Math.max(max, n.startTicks + n.durationTicks),
      0
    );
  }, [playback.notes]);
  const mbt = tickToMBT(currentTick, midi.header.ppq, timeSigs, totalTicks);

  const isPlaying = status === "playing";

  return (
    <section className={`card ${s.player}`}>
      <div className={s.head}>
        <h2 className="card__title">Прослушать в браузере</h2>
        <div className={s.headRight}>
          <span className="muted">{fileName}</span>
          <TimeBox measure={mbt.measure} beat={mbt.beat} tick={mbt.tick} />
        </div>
      </div>
      {!isMidi && engineState.isLoading ? (
        <p className={s.loading}>
          Загрузка звуков: {engineState.progress.loaded} /{" "}
          {engineState.progress.total}…
        </p>
      ) : null}
      {!isMidi && engineState.error ? (
        <div className={`banner banner--error ${s.error}`}>
          <span>{engineState.error}</span>
        </div>
      ) : null}
      {isMidi && midiPortInstance === null ? (
        <div className={`banner banner--error ${s.error}`}>
          <span>
            MIDI-порт недоступен. Подключите устройство или GM-синтезатор в
            настройках (⚙ Звук → «MIDI-устройство»).
          </span>
        </div>
      ) : null}
      <div className={s.controls}>
        <button
          type="button"
          className="btn btn--primary"
          onClick={isPlaying ? handlePause : handlePlay}
          disabled={pendingPlay}
          aria-label={isPlaying ? "Пауза" : "Воспроизвести"}
        >
          {isPlaying ? "❚❚ Пауза" : pendingPlay ? "▶ Загрузка…" : "▶ Воспроизвести"}
        </button>
        <button
          type="button"
          className="btn"
          onClick={handleStop}
          disabled={status === "idle" && !pendingPlay}
          aria-label="Стоп"
        >
          ■ Стоп
        </button>
        <input
          type="range"
          className={s.progress}
          min={0}
          max={Math.max(durationMs, 0)}
          step={50}
          value={positionMs}
          onChange={handleSeek}
          disabled={playback.notes.length === 0 || durationMs === 0}
          aria-label="Перемотка"
        />
        {!isMidi ? (
          <label className={s.volume}>
            <span className="muted">🔊</span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={volume}
              onChange={handleVolume}
              aria-label="Громкость"
            />
          </label>
        ) : null}
        {onOpenSettings ? (
          <button
            type="button"
            className="btn btn--ghost"
            onClick={onOpenSettings}
            aria-label="Изменить источник звука"
          >
            ⚙ Звук
          </button>
        ) : null}
      </div>
    </section>
  );
};

const EMPTY_ENGINE_STATE: EngineState = {
  isLoading: false,
  isReady: false,
  error: null,
  progress: { loaded: 0, total: 0 },
  failedChannels: [],
  resolvedPresets: new Map(),
};